import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { 
  ScanSearch, Play, Clock, AlertTriangle, CheckCircle, 
  ChevronDown, ChevronUp, Server, FileWarning, Shield
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const riskColors = {
  critical: "bg-red-500/20 text-red-400 border-red-500/30",
  high: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  medium: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  low: "bg-green-500/20 text-green-400 border-green-500/30"
};

function ScanReportCard({ report }) {
  const [expanded, setExpanded] = useState(false);
  const startedAt = new Date(report.started_at).toLocaleString();
  const completedAt = report.completed_at ? new Date(report.completed_at).toLocaleString() : "N/A";

  return (
    <Card className="bg-[#0c0c0e] border-white/5" data-testid={`scan-report-${report.id}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              report.critical_findings > 0 ? 'bg-red-500/10' : report.high_findings > 0 ? 'bg-orange-500/10' : 'bg-green-500/10'
            }`}>
              {report.critical_findings > 0 ? (
                <AlertTriangle className="w-5 h-5 text-red-400" />
              ) : report.high_findings > 0 ? (
                <FileWarning className="w-5 h-5 text-orange-400" />
              ) : (
                <CheckCircle className="w-5 h-5 text-green-400" />
              )}
            </div>
            <div>
              <p className="text-white text-sm font-medium">
                Security Scan - {report.type === "nightly_security_scan" ? "Nightly" : "Manual"}
              </p>
              <p className="text-xs text-zinc-500">{startedAt}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex gap-2 text-center">
              <div className="px-2">
                <p className="text-lg font-bold text-white">{report.servers_scanned}</p>
                <p className="text-xs text-zinc-500">Servers</p>
              </div>
              <div className="px-2">
                <p className="text-lg font-bold text-red-400">{report.critical_findings}</p>
                <p className="text-xs text-zinc-500">Critical</p>
              </div>
              <div className="px-2">
                <p className="text-lg font-bold text-orange-400">{report.high_findings}</p>
                <p className="text-xs text-zinc-500">High</p>
              </div>
              <div className="px-2">
                <p className="text-lg font-bold text-yellow-400">{report.medium_findings || 0}</p>
                <p className="text-xs text-zinc-500">Medium</p>
              </div>
            </div>
            <Button
              size="sm" variant="ghost"
              onClick={() => setExpanded(!expanded)}
              className="text-zinc-400 hover:text-white"
              data-testid={`expand-report-${report.id}`}
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {expanded && report.findings && (
          <div className="mt-4 space-y-2 border-t border-white/5 pt-4">
            {report.findings.length === 0 ? (
              <p className="text-sm text-zinc-500 text-center py-3">No findings in this scan</p>
            ) : (
              report.findings.map((finding, idx) => (
                <div key={finding.id || idx} className="p-3 bg-black/40 rounded-lg border border-white/5" data-testid={`finding-${finding.id || idx}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Badge className={`text-xs ${riskColors[finding.risk_level]}`}>
                        {finding.risk_level?.toUpperCase()}
                      </Badge>
                      <span className="text-sm text-white">{finding.check_name}</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-zinc-500">
                      <Server className="w-3 h-3" />
                      {finding.server_name}
                    </div>
                  </div>
                  <pre className="text-xs text-zinc-400 bg-black/60 rounded p-2 overflow-x-auto max-h-24 overflow-y-auto">
                    {finding.output || "No output"}
                  </pre>
                </div>
              ))
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function SecurityScans() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [servers, setServers] = useState([]);
  const [selectedServer, setSelectedServer] = useState("all");
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    fetchReports();
    fetchSchedule();
    fetchServers();
  }, []);

  const fetchServers = async () => {
    try {
      const res = await axios.get(`${API}/servers`);
      setServers(res.data.filter(s => s.ssh_configured));
    } catch (err) {
      console.error("Failed to fetch servers:", err);
    }
  };

  const fetchReports = async () => {
    try {
      const res = await axios.get(`${API}/security-scan/reports`);
      setReports(res.data);
    } catch (err) {
      console.error("Failed to fetch scan reports:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSchedule = async () => {
    try {
      const res = await axios.get(`${API}/security-scan/schedule`);
      setSchedule(res.data);
    } catch (err) {
      console.error("Failed to fetch schedule:", err);
    }
  };

  const triggerScan = async () => {
    setScanning(true);
    try {
      const params = selectedServer !== "all" ? `?server_id=${selectedServer}` : "";
      await axios.post(`${API}/security-scan/trigger${params}`);
      const serverLabel = selectedServer !== "all" ? servers.find(s => s.id === selectedServer)?.name : "all servers";
      toast.success(`Scan triggered for ${serverLabel}! Results will appear shortly.`);
      setTimeout(fetchReports, 5000);
      setTimeout(fetchReports, 15000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to trigger scan");
    } finally {
      setScanning(false);
    }
  };

  if (user?.role === "junior_admin") {
    return (
      <div className="p-6 max-w-4xl mx-auto" data-testid="security-scans-page">
        <Card className="bg-[#0c0c0e] border-white/5">
          <CardContent className="py-12 text-center">
            <Shield className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-400">Senior admin or admin access required to view security scans.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="security-scans-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-mono">Security Scans</h1>
          <p className="text-zinc-400 text-sm mt-1">Automated and manual security scan results</p>
        </div>
        <div className="flex items-center gap-3">
          {schedule && (
            <div className="flex items-center gap-2 px-3 py-2 bg-black/30 rounded-lg border border-white/5">
              <Clock className="w-4 h-4 text-blue-400" />
              <span className="text-sm text-zinc-400">{schedule.schedule}</span>
              <Badge className={schedule.enabled ? "bg-green-500/20 text-green-400 border-green-500/30 text-xs" : "bg-zinc-500/20 text-zinc-400 border-zinc-500/30 text-xs"}>
                {schedule.enabled ? "Active" : "Inactive"}
              </Badge>
            </div>
          )}
          <Select value={selectedServer} onValueChange={setSelectedServer}>
            <SelectTrigger className="w-48 bg-black/50 border-white/10 text-white text-sm" data-testid="scan-server-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0c0c0e] border-white/10">
              <SelectItem value="all">All SSH Servers</SelectItem>
              {servers.map(s => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={triggerScan} disabled={scanning} className="bg-blue-600 hover:bg-blue-700" data-testid="trigger-scan-btn">
            {scanning ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                Scanning...
              </>
            ) : (
              <>
                <Play className="w-4 h-4 mr-2" />
                Run Scan Now
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      {reports.length > 0 && (
        <div className="grid grid-cols-4 gap-4">
          <Card className="bg-[#0c0c0e] border-white/5">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-white">{reports.length}</p>
              <p className="text-xs text-zinc-500">Total Scans</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0c0c0e] border-white/5">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-red-400">
                {reports.reduce((sum, r) => sum + (r.critical_findings || 0), 0)}
              </p>
              <p className="text-xs text-zinc-500">Total Critical</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0c0c0e] border-white/5">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-orange-400">
                {reports.reduce((sum, r) => sum + (r.high_findings || 0), 0)}
              </p>
              <p className="text-xs text-zinc-500">Total High</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0c0c0e] border-white/5">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-400">
                {reports.reduce((sum, r) => sum + (r.servers_scanned || 0), 0)}
              </p>
              <p className="text-xs text-zinc-500">Servers Scanned</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Reports */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : reports.length === 0 ? (
        <Card className="bg-[#0c0c0e] border-white/5">
          <CardContent className="py-12 text-center">
            <ScanSearch className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-400">No scan reports yet. Run your first scan or wait for the nightly scan.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {reports.map(report => (
            <ScanReportCard key={report.id} report={report} />
          ))}
        </div>
      )}
    </div>
  );
}
